import { useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { LuCalendar as Calendar } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataList, PageHeader } from '@minetrace/ui'
import { brand, themeColors } from '../../../theme'
import { programacionesService } from '../../../api/services'
import { usePagedRequest } from '../../../hooks'
import type { InformeVerificacionListItemResponse } from '../../../api/models/response/programacion'
import { DETAIL_STATUSES } from '../../../data/enums'
import { exportToExcel, type ExcelColumn } from '../../../utils/exportExcel'

// DetailStatus values that this page can show.
const STATUS_COMPLETADO = 3
const STATUS_FINALIZADO = 4
const STATUS_APROBADA = 6
const STATUS_RECHAZADA = 7

const STATUS_COLORS: Record<number, { bg: string; color: string }> = {
  [STATUS_COMPLETADO]: { bg: 'rgba(212, 154, 42, 0.15)', color: '#8a5f0b' }, // pending review
  [STATUS_FINALIZADO]: { bg: 'rgba(212, 154, 42, 0.15)', color: '#8a5f0b' }, // pending review (post-acta)
  [STATUS_APROBADA]: { bg: 'rgba(42, 138, 135, 0.15)', color: '#1d6664' },
  [STATUS_RECHAZADA]: { bg: 'rgba(200, 68, 47, 0.12)', color: '#9a3020' },
}

const FILTERS: { value: number | undefined; label: string }[] = [
  { value: undefined, label: 'Todos' },
  { value: STATUS_COMPLETADO, label: 'Pendientes de revisión' },
  { value: STATUS_APROBADA, label: 'Aprobadas' },
  { value: STATUS_RECHAZADA, label: 'Rechazadas' },
]

const excelColumns: ExcelColumn<InformeVerificacionListItemResponse>[] = [
  { header: 'Programación', accessor: (r) => r.scheduleCode ?? '-' },
  { header: 'Minero', accessor: (r) => r.companyName ?? '-' },
  { header: 'RUC', accessor: (r) => r.companyRuc ?? '-' },
  { header: 'Derecho Minero', accessor: (r) => r.miningRightName ?? '-' },
  { header: 'Código DM', accessor: (r) => r.miningRightCode ?? '-' },
  { header: 'Geólogo', accessor: (r) => r.geologistFullName ?? '-' },
  { header: 'Completada', accessor: (r) => r.completedAt ?? '' },
  { header: 'Estado', accessor: (r) => DETAIL_STATUSES.find((s) => s.value === r.detailStatus)?.label ?? '-' },
]

const formatDate = (iso: string | null) => {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return '-'
  }
}

export default function InformesVerificacionPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<number | undefined>(STATUS_COMPLETADO)

  const { items, totalCount, isFetching: loading, error, page, setPage, totalPages, pageSize, setPageSize } = usePagedRequest(
    programacionesService.getInformes,
    { params: { search: search.trim() || undefined, status: statusFilter } },
  )

  return (
    <VStack gap={5} alignItems="stretch">
      <PageHeader
        title="Informes de Verificación"
        subtitle="Revisa y aprueba o rechaza las visitas de campo completadas por los geólogos."
        colors={colors}
      />

      {/* Status filter chips */}
      <Flex gap={2} flexWrap="wrap">
        {FILTERS.map((f) => {
          const active = statusFilter === f.value
          return (
            <Box
              key={f.label}
              as="button"
              px={3}
              py="6px"
              borderRadius="full"
              fontSize="xs"
              fontWeight="600"
              cursor="pointer"
              border="1px solid"
              borderColor={active ? brand.green : 'gray.200'}
              bg={active ? brand.green : 'white'}
              color={active ? 'white' : '#4f4842'}
              _hover={{ bg: active ? brand.green : 'gray.50' }}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </Box>
          )
        })}
      </Flex>

      <DataList<InformeVerificacionListItemResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por programación, minero, RUC o derecho minero..."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        entityName="informes"
        onExport={() => exportToExcel(items, excelColumns, 'informes-verificacion')}
        brandColor={brand.green}
        headers={[
          'Programación',
          'Minero',
          'Derecho Minero',
          'Geólogo',
          'Visita',
          'Estado',
        ]}
        data={items}
        loading={loading}
        error={error}
        emptyMessage="No hay visitas que cumplan con el filtro seleccionado."
        loadingMessage="Cargando informes..."
        colors={colors}
        keyExtractor={(r) => r.detailId}
        renderRow={(r) => (
          <>
            <DataTable.Cell>
              <Text fontFamily="'JetBrains Mono', monospace" fontSize="sm" fontWeight="600" color={colors.text}>
                {r.scheduleCode ?? '-'}
              </Text>
              {r.scheduleName && (
                <Text fontSize="xs" color={colors.textMuted}>
                  {r.scheduleName}
                </Text>
              )}
            </DataTable.Cell>

            <DataTable.Cell>
              <Box>
                <Text fontSize="sm" fontWeight="600" color={colors.text}>
                  {r.companyName ?? '-'}
                </Text>
                {r.companyRuc && (
                  <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">
                    RUC {r.companyRuc}
                  </Text>
                )}
              </Box>
            </DataTable.Cell>

            <DataTable.Cell>
              <Box>
                <Text fontSize="sm" color={colors.text}>
                  {r.miningRightName ?? '-'}
                </Text>
                {r.miningRightCode && (
                  <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">
                    {r.miningRightCode}
                  </Text>
                )}
              </Box>
            </DataTable.Cell>

            <DataTable.Cell>
              <Text fontSize="sm" color={colors.text}>
                {r.geologistFullName ?? '-'}
              </Text>
            </DataTable.Cell>

            <DataTable.Cell>
              <Flex alignItems="center" gap={1.5}>
                <Box color={colors.textMuted} flexShrink={0}>
                  <Calendar size={14} />
                </Box>
                <Text fontSize="sm" color={colors.text}>
                  {formatDate(r.completedAt)}
                </Text>
              </Flex>
            </DataTable.Cell>

            <DataTable.Cell>
              <Box
                display="inline-block"
                px={2.5}
                py={1}
                borderRadius="full"
                fontSize="10px"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="0.06em"
                bg={STATUS_COLORS[r.detailStatus]?.bg ?? 'rgba(181, 170, 157,0.15)'}
                color={STATUS_COLORS[r.detailStatus]?.color ?? '#5f574f'}
              >
                {DETAIL_STATUSES.find((s) => s.value === r.detailStatus)?.label ?? '-'}
              </Box>
            </DataTable.Cell>
          </>
        )}
        actions={(r) => [
          {
            label: r.detailStatus === STATUS_COMPLETADO || r.detailStatus === STATUS_FINALIZADO ? 'Revisar' : 'Ver detalle',
            onClick: () => navigate(`/app/programaciones/${r.scheduleId}/details/${r.detailId}/visita`),
          },
        ]}
      />
    </VStack>
  )
}
